import type { JSX } from 'react';
import { DateTime } from '@ksyos/date-time';
import {
    Button,
    ButtonRow,
    Card,
    Collapse,
    FormGroup,
    Link,
    PageHeader,
    PageLayout,
    Select,
} from '@ksyos/design-system';
import { useEffect, useState } from 'react';
import type { DatoExportLog } from '@common/interfaces/api/bariatricsApi';
import { ToastType } from '@common/enums';
import { DatoExportType } from '@common/enums/datoExportType';
import { sleep } from '@common/utils/sleep';
import { MonthPicker } from '@frontend/components/elements/MonthPicker';
import { exportForDato, getDatoExportLogs } from '@frontend/services/api/bariatricsApiService';
import { getSelectedLanguage } from '@frontend/translate';
import { showToast } from '@frontend/utils';
import { useAbortSignal } from '@frontend/utils/hooks/useAbortSignal';
import { DatoExportLogsTable } from './DatoExportLogsTable';
import { datoExportTypeOptions } from './datoUtils';

const activityTypeOptions = [
    { label: datoExportTypeOptions[DatoExportType.screening].activityType, value: DatoExportType.screening },
    { label: datoExportTypeOptions[DatoExportType.followUp].activityType, value: DatoExportType.followUp },
];

const dateFormat = 'YYYY-MM-DD';

export function ExportForDato(): JSX.Element {
    const abortSignal = useAbortSignal();
    const [datoExportLogs, setDatoExportLogs] = useState<DatoExportLog[]>([]);
    const [datoExportLogsLimit, setDatoExportLogsLimit] = useState(10);
    const [datoExportLogsPage, setDatoExportLogsPage] = useState(1);
    const [datoExportLogsTotal, setDatoExportLogsTotal] = useState(0);
    const [exportDate, setExportDate] = useState<Date>(DateTime.now().startOf('month').toDate());
    const [isLoadingDatoExportLogs, setIsLoadingDatoExportLogs] = useState(false);
    const [showExportHistory, setShowExportHistory] = useState<boolean>(false);
    const [type, setType] = useState<DatoExportType>();

    useEffect(() => {
        loadDatoExportLogs();
    }, [datoExportLogsLimit, datoExportLogsPage, showExportHistory]);

    return (
        <PageLayout>
            <PageHeader title={getSelectedLanguage().common.export} />
            <Card>
                <Card.Header title={getSelectedLanguage().export.exportForDato} />
                <Card.Body>
                    <FormGroup
                        isRequired={true}
                        label="Type activiteit"
                    >
                        <Select
                            onChange={(newValue) => setType(newValue?.value)}
                            options={activityTypeOptions}
                            placeholder="Selecteer een activiteit..."
                            value={activityTypeOptions.find((option) => option.value === type)}
                        />
                    </FormGroup>
                    {type && (
                        <>
                            <FormGroup
                                isRequired={true}
                                label={datoExportTypeOptions[type].monthLabel}
                                tooltipText={datoExportTypeOptions[type].tooltipText}
                            >
                                <MonthPicker
                                    minValue={DateTime.fromObject({ year: 2023 }).toDate()}
                                    onChange={(newValue) => setExportDate(newValue)}
                                    selectedDate={exportDate}
                                />
                            </FormGroup>
                            <ButtonRow justify="right">
                                <Button onClick={() => handleClickExport(type)}>Exporteer</Button>
                            </ButtonRow>
                        </>
                    )}
                </Card.Body>

                {datoExportLogs.length > 0 && (
                    <>
                        <Collapse isOpen={showExportHistory}>
                            <DatoExportLogsTable
                                datoExportLogs={datoExportLogs}
                                isLoading={isLoadingDatoExportLogs}
                                limit={datoExportLogsLimit}
                                onChangeLimit={(newLimit) => setDatoExportLogsLimit(newLimit)}
                                onChangePage={(newPage) => setDatoExportLogsPage(newPage)}
                                page={datoExportLogsPage}
                                total={datoExportLogsTotal}
                            />
                        </Collapse>
                        <Card.Footer>
                            <Link
                                color="subtle"
                                fontWeight="normal"
                                iconLeft={showExportHistory ? 'caret-up' : 'caret-down'}
                                onClick={() => setShowExportHistory(!showExportHistory)}
                                variant="button"
                            >
                                {showExportHistory
                                    ? getSelectedLanguage().export.exportHistoryHide
                                    : getSelectedLanguage().export.exportHistoryShow}
                            </Link>
                        </Card.Footer>
                    </>
                )}
            </Card>
        </PageLayout>
    );

    async function loadDatoExportLogs(): Promise<void> {
        setIsLoadingDatoExportLogs(true);

        const { items, total } = await getDatoExportLogs(
            { limit: datoExportLogsLimit, page: datoExportLogsPage },
            abortSignal
        );

        setDatoExportLogs(items);
        setDatoExportLogsTotal(total);
        setIsLoadingDatoExportLogs(false);
    }

    async function handleClickExport(datoExportType: DatoExportType): Promise<void> {
        exportForDato({
            fromDate: DateTime.from(exportDate).startOf('month').format(dateFormat),
            type: datoExportType,
            untilDate: DateTime.from(exportDate).add(1, 'month').format(dateFormat),
        });
        showToast(ToastType.info, 'Exporteren gestart');

        if (datoExportLogs.length === 0 || showExportHistory) {
            // Arbitrary timeout before reloading, because we don't know when the export is done
            await sleep(3000);
            await loadDatoExportLogs();
        }
    }
}
